import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../../lib/cn";

/** Inputs sit in the inset well so the surface above them reads as the page, not the form. */
const control =
  "field rounded-control border-line bg-well text-ink w-full border px-3 text-ui disabled:opacity-50";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...props }, ref) {
    return <input ref={ref} className={cn(control, "h-8", className)} {...props} />;
  },
);

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={cn(control, "min-h-20 py-2 leading-relaxed", className)}
      {...props}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...props }, ref) {
    return (
      <span className="relative block">
        <select
          ref={ref}
          className={cn(control, "h-8 appearance-none pr-8", className)}
          {...props}
        />
        <CaretDownIcon className="text-faint pointer-events-none absolute top-1/2 right-2.5 size-3 -translate-y-1/2" />
      </span>
    );
  },
);

export const Checkbox = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode }
>(function Checkbox({ label, className, ...props }, ref) {
  return (
    <label
      className={cn(
        "group text-ui text-body inline-flex items-center gap-2.5 select-none has-disabled:opacity-50",
        className,
      )}
    >
      <span className="border-line bg-well ease-out-expo group-hover:border-line-strong has-[:checked]:border-accent has-[:checked]:bg-accent relative grid size-4 shrink-0 place-items-center rounded-[5px] border transition-colors duration-200">
        <input
          ref={ref}
          type="checkbox"
          className="peer absolute inset-0 appearance-none"
          {...props}
        />
        <CheckIcon
          weight="bold"
          className="text-accent-ink size-2.5 opacity-0 transition-opacity peer-checked:opacity-100"
        />
      </span>
      {label}
    </label>
  );
});

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-caption text-body font-medium">{label}</span>
      {children}
      {error ? (
        <span className="text-caption text-danger-text">{error}</span>
      ) : hint ? (
        <span className="text-caption text-faint">{hint}</span>
      ) : null}
    </label>
  );
}
